import React from 'react';
import styled from 'styled-components';
import { groupByCategory } from '../services/nearbyPlacesService';

const Wrap = styled.div`
  background: var(--mf-bg-card);
  border: 1px solid var(--mf-border);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 16px;
`;

const Row = styled.div`
  border-bottom: 1px solid var(--mf-border);
  &:last-child { border-bottom: none; }
`;

const HeadRow = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr auto auto 24px;
  align-items: center;
  gap: 14px;
  padding: 14px 22px;
  background: ${p => p.$open ? 'var(--mf-bg-alt)' : 'transparent'};
  cursor: pointer;
  font-family: inherit;
  transition: background .15s;
  &:hover { background: var(--mf-bg-alt); }
  .icon { font-size: 20px; line-height: 1; }
  .label {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--mf-text-primary);
  }
  .count {
    font-size: 12px;
    color: var(--mf-text-secondary);
    letter-spacing: 0.5px;
  }
  .chev {
    color: var(--mf-text-secondary);
    transition: transform .2s;
    transform: ${p => p.$open ? 'rotate(180deg)' : 'none'};
    font-size: 12px;
    line-height: 1;
  }
`;

const EyeBtn = styled.button`
  background: transparent;
  border: 1px solid ${p => p.$on ? 'var(--mf-accent-warm)' : 'var(--mf-border)'};
  color: ${p => p.$on ? 'var(--mf-accent-warm)' : 'var(--mf-text-secondary)'};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all .15s;
  &:hover {
    border-color: var(--mf-accent-warm);
    color: var(--mf-accent-warm);
  }
`;

const Body = styled.div`
  display: ${p => p.$open ? 'block' : 'none'};
  padding: 8px 22px 18px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed var(--mf-border);
  font-size: 14px;
  &:last-child { border-bottom: none; }
  .name { color: var(--mf-text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .dist {
    color: var(--mf-accent-warm);
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
  }
`;

const State = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--mf-text-secondary);
  font-size: 13px;
`;

function formatDist(km) {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

/**
 * NearbyPlaces — exibe accordion de lugares próximos.
 * É um componente controlado: recebe places + visibility + onToggle do parent.
 *
 * Props:
 *  - places: array de lugares com { id, name, distance, category, ... }
 *  - loading: bool
 *  - error: string|null
 *  - visibility: Map<categoryId, bool>
 *  - onToggleVisibility: (categoryId) => void
 *  - openCategories: Set<categoryId>
 *  - onToggleOpen: (categoryId) => void
 *  - maxPerCategory: number (default 8)
 */
export default function NearbyPlaces({
  places,
  loading,
  error,
  visibility,
  onToggleVisibility,
  openCategories,
  onToggleOpen,
  maxPerCategory = 8,
}) {
  if (loading) return <State>Buscando lugares próximos…</State>;
  if (error) return <State>Não consegui carregar lugares próximos ({error}).</State>;
  if (!places || places.length === 0) return <State>Nenhum lugar encontrado nas proximidades.</State>;

  const grouped = groupByCategory(places);

  return (
    <Wrap>
      {grouped.map(g => {
        const isOpen = openCategories?.has?.(g.id) ?? false;
        const isVisible = visibility?.get?.(g.id) ?? true;
        const items = g.items.slice(0, maxPerCategory);
        return (
          <Row key={g.id}>
            <HeadRow
              $open={isOpen}
              onClick={() => onToggleOpen?.(g.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleOpen?.(g.id); } }}
            >
              <span className="icon">{g.icon}</span>
              <span className="label">{g.label}</span>
              <span className="count">{g.items.length} {g.items.length === 1 ? 'lugar' : 'lugares'}</span>
              <EyeBtn
                type="button"
                $on={isVisible}
                onClick={(e) => { e.stopPropagation(); onToggleVisibility?.(g.id); }}
                title={isVisible ? 'Ocultar no mapa' : 'Mostrar no mapa'}
                aria-label={isVisible ? 'Ocultar no mapa' : 'Mostrar no mapa'}
              >
                {isVisible ? '👁' : '⊘'}
              </EyeBtn>
              <span className="chev">▼</span>
            </HeadRow>
            <Body $open={isOpen}>
              {items.map(p => (
                <Item key={p.id}>
                  <span className="name">{p.name}</span>
                  <span className="dist">{formatDist(p.distance)}</span>
                </Item>
              ))}
              {g.items.length > maxPerCategory && (
                <Item style={{ color: 'var(--mf-text-secondary)', fontSize: 12, fontStyle: 'italic' }}>
                  <span className="name">+ {g.items.length - maxPerCategory} mais nesta categoria</span>
                  <span className="dist" />
                </Item>
              )}
            </Body>
          </Row>
        );
      })}
    </Wrap>
  );
}
